<template>
  <div
      v-if="position && content !== ''"
      ref="labelContainer"
      class="absolute w-[50%] h-[50%] pointer-events-none"
      :class="[
            position === 'tl' && 'top-0 left-0',
            position === 'tr' && 'top-0 right-0',
            position === 'br' && 'bottom-0 right-0',
            position === 'bl' && 'bottom-0 left-0',
        ]"
  >
    <div
        class="h-full absolute"
        :class="[
                position === 'tl' && 'top-0 left-0',
                position === 'tr' && 'top-0 right-0',
                position === 'br' && 'bottom-0 right-0',
                position === 'bl' && 'bottom-0 left-0',
            ]"
        :style="{ height: size + '%' }"
    >
      <div
          class="absolute shadow"
          :class="[
                    ['tr', 'br'].includes(position) && 'right-[12px]',
                    ['tl', 'bl'].includes(position) && 'left-[0px]',
                    ['tl', 'tr'].includes(position) && 'top-[0px]',
                    ['bl', 'br'].includes(position) && 'bottom-[12px]',
                ]"
          :style="{ height: '100%' }"
      >
        <div
            ref="textContainerContent"
            class="h-full px-3 flex items-center font-medium text-[14px] leading-[16px] whitespace-nowrap rounded-[5px]"
            :class="[
                        'text-white',
                        featureType.includes('is_active_discount') && 'bg-hot-pink',
                        featureType.includes('is_featured') && 'bg-amber',
                        featureType.includes('is_new')  && 'bg-lime-green',
                        featureType.length === 0 && 'bg-lime-green',
                    ]"
        >
          {{ content }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {ref, onMounted, onBeforeUnmount, nextTick, watch, computed} from 'vue';

const props = defineProps({
  position: {
    type:    String,
    default: 'tl',
  },
  size:     {
    type:    Number,
    default: 24,
  },
  content:  {
    type:    String,
    default: '',
  },
  options:  {
    type:    Object,
    default: () => {
    },
  }
});

const featureType = computed(() => {

  if (!props.options) return []
  const types = [
    'is_new',
    'is_featured',
    'is_popular',
    'is_active_discount',
    'is_free_eu_shipping'
  ];

  return types.filter(type => props.options[type] === true);
});

const CONSOLE_MESSAGES = {
  MISSING_CONTAINERS:
      'Cannot initialize label - missing label container or text container!',
};

const labelContainer = ref(null);
const textContainerContent = ref(null);
const fontFamily = ref(null);
const defaultFontSize = ref(12);

const setLabel = async () => {
  try {
    await nextTick();
    adjustFontSize();
  } catch (error) {
    console.error('An unexpected error occurred in setLabel:', error);
  }
};

const adjustFontSize = () => {
  try {
    const lc = labelContainer.value?.getBoundingClientRect();
    const tc = textContainerContent.value;
    const ff = fontFamily.value;
    const padding = 12;
    const dff = defaultFontSize.value;

    if (!lc || !tc) {
      console.error(CONSOLE_MESSAGES.MISSING_CONTAINERS);
      return;
    }

    const availableSpace = lc.width;
    const textContentWidth = measureTextWidth(props.content, dff, ff) + padding;

    if (textContentWidth > availableSpace) {
      const adjustedFontSize = (availableSpace / textContentWidth) * dff;
      tc.style.fontSize = `${adjustedFontSize}px`;
    } else {
      tc.style.fontSize = `${dff}px`;
    }
  } catch (error) {
    console.error('An unexpected error occurred in adjustFontSize:', error);
  }
};

const measureTextWidth = (text, fontSize, fontFamily) => {
  const tempSpan = document.createElement('span');
  tempSpan.style.fontSize = `${fontSize}px`;
  tempSpan.style.fontFamily = fontFamily;
  tempSpan.textContent = text;
  document.body.appendChild(tempSpan);
  const width = tempSpan.offsetWidth;
  document.body.removeChild(tempSpan);
  return width;
};

const extractFontFamily = () => {
  const element = textContainerContent.value;
  if (element) {
    const computedStyle = window.getComputedStyle(element);
    const fontFamily = computedStyle.fontFamily;
    const fontFamiliesArray = fontFamily.split(',');
    const trimmedFontFamilies = fontFamiliesArray.map((font) => font.trim());
    return trimmedFontFamilies;
  } else {
    return [];
  }
};

const extractFontSize = () => {
  const element = textContainerContent.value;
  if (!element) return defaultFontSize.value;
  const computedStyle = window.getComputedStyle(element);
  return parseInt(computedStyle.fontSize, 10);
};

onMounted(() => {
  defaultFontSize.value = extractFontSize();

  const fontFamilies = extractFontFamily();

  if (fontFamilies && fontFamilies.length) {
    fontFamily.value = fontFamilies[0];
  }

  setLabel();

  window.addEventListener('resize', setLabel);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', setLabel);
});

watch(() => props.content, setLabel);
watch(() => props.size, setLabel);
watch(() => props.position, setLabel);
</script>
